import {
  Button,
  Center,
  Group,
  Loader,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconEdit,
  IconDownload,
  IconFileDownload,
  IconMapPlus,
  IconTrash,
} from '@tabler/icons-react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FieldMapsLayout } from '../../../../components/field-maps-layout/field-maps-layout';
import SelectMapType from '../../../../components/select-map-type/select-map-type';
import useSharedFieldMaps from '../../../../hooks/use-shared-field-maps/shared-field-maps';
import { MAP_DISPLAY_TYPES } from '../compare-maps/page';

import { FORM_STATUS } from '@advices/data-access';
import { useAuth } from '@auth/data-access';
import { hasAdminAccess, PRESCRIPTION_FROM_TYPES } from '@data-access';
import { EDITTABLE_MAP_TYPES, fieldMapsApi } from '@fields/data-access';
import {
  LegendControl,
  PfMap,
  ZoningLayer,
  ZoningLegend,
  ZoningLegendProps,
} from '@map';
import { ActionsMenu, ActionsMenuItem, TsrButtonLink } from '@ui';

interface SoilMapProps {
  type: MAP_DISPLAY_TYPES;
}

export function SoilMap({ type }: SoilMapProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { field, fieldId, mapId, compare, queries, handleCompareChange } =
    useSharedFieldMaps({ type });

  const summary = useSuspenseQuery(queries.soilSummary(Number(fieldId)));
  const [selectedMap, setSelectedMap] = useState<string | null>(
    mapId?.toString() ?? summary.data.options[0]?.value ?? null,
  );
  const soilMap = useQuery({
    ...queries.soilMap(Number(selectedMap), Number(fieldId)),
    enabled: selectedMap !== null,
    placeholderData: keepPreviousData,
    refetchInterval(query) {
      return query.state.data?.status !== FORM_STATUS.FINISHED ? 5000 : false;
    },
  });
  const deleteMap = useMutation({
    mutationFn: fieldMapsApi.deleteSoilmap,
    onSuccess(data, variables, context) {
      const filtered = summary.data.options.filter(
        (option) => option.value !== selectedMap,
      );
      // select first map in list
      setSelectedMap(filtered[0]?.value ?? null);
    },
  });
  const exportMap = useMutation({
    mutationFn: fieldMapsApi.exportSoilMap,
  });
  const recap = useQuery({
    ...queries.soilRecap(Number(selectedMap)),
    enabled: true,
  });

  const legend = (): ZoningLegendProps => {
    const map = soilMap.data?.map;
    if (map) {
      const l = map.features.reduce((acc, feature) => {
        return {
          ...acc,
          [feature.properties.zone]: {
            zone: feature.properties.zone,
            color: feature.properties.color,
            value: feature.properties.value,
            percent: feature.properties.percent,
            area: feature.properties.area,
          },
        };
      }, {});

      const unit = soilMap.data?.soil_map_type.unit;
      const name = soilMap.data?.soil_map_type.name;

      return {
        title: `${name} ${unit ? `(${unit})` : ''}`,
        legend: Object.values(l),
        unit: soilMap.data?.soil_map_type.unit ?? '',
      };
    }
    return {
      legend: [],
    };
  };

  const adminItems: ActionsMenuItem[] = [
    {
      icon: IconMapPlus,
      label: t('common.add-map'),
      type: 'link',
      props: {
        to: '/fields/$fieldId/maps/$editableMapType/create',
        params: {
          fieldId,
          editableMapType: EDITTABLE_MAP_TYPES.SOIL,
        },
      },
    },
    {
      type: 'action',
      label: t('common.export'),
      icon: IconDownload,
      props: {
        disabled: selectedMap === null || exportMap.isPending,
      },
      onClick: () => {
        if (!selectedMap) return;
        exportMap.mutate(Number(selectedMap));
      },
    },
    {
      icon: IconEdit,
      label: t('common.edit'),
      props: {
        disabled: true,
      },
      type: 'action',
      onClick: () => null,
    },
    {
      icon: IconTrash,
      label: t('common.delete'),
      color: 'red',
      type: 'action',
      props: {
        disabled: selectedMap === null,
      },
      onClick() {
        modals.openConfirmModal({
          title: t('common.delete-map'),
          children: t('common.delete-map-confirm'),
          labels: { confirm: t('common.delete'), cancel: t('common.cancel') },

          onConfirm: () => deleteMap.mutate(Number(selectedMap)),
        });
      },
    },
  ];

  const baseItems: ActionsMenuItem[] = [
    {
      type: 'native-link',
      label: 'Télécharger le récapitulatif',
      icon: IconFileDownload,
      props: {
        disabled: !recap.data?.file || recap.isLoading,
        target: '_blank',
        href: recap.data?.file,
      },
    },
  ];

  const items: ActionsMenuItem[] = hasAdminAccess(user?.user_access)
    ? [...baseItems, ...adminItems]
    : baseItems;

  return (
    <FieldMapsLayout
      content={
        <>
          {soilMap.data?.status === FORM_STATUS.FINISHED ? (
            <PfMap zoomToGeojson={field.data}>
              <ZoningLayer data={soilMap.data.map} filled outline={false} />
              <LegendControl>
                <ZoningLegend {...legend()} />
              </LegendControl>
            </PfMap>
          ) : null}

          {soilMap.data?.status === FORM_STATUS.PENDING ? (
            <Center h="100%" p="xl">
              <Stack align="center">
                <Text fw="bold" size="xl" ta="center">
                  {t('common.preparing-map')}
                </Text>
                <Loader />
              </Stack>
            </Center>
          ) : null}

          {summary.data?.options.length === 0 ? (
            <Center h="100%" p="xl">
              <Stack>
                <Text fw="bold" size="xl" ta="center">
                  {t('common.no-soil-map-available')}
                </Text>
                <Button>{t('common.contact-us')}</Button>
              </Stack>
            </Center>
          ) : null}
        </>
      }
      header={
        <Group h="100%" justify="space-between" p="md">
          <Group>
            {type !== 'single' ? (
              <SelectMapType
                maps={field.data.properties.maps}
                onChange={handleCompareChange}
                value={type === 'compare-left' ? compare?.[0] : compare?.[1]}
              />
            ) : null}

            {summary.data?.options.length ? (
              <Select
                allowDeselect={false}
                data={summary.data?.options}
                onChange={setSelectedMap}
                rightSection={soilMap.isFetching ? <Loader size="xs" /> : null}
                value={selectedMap}
              />
            ) : null}
          </Group>
          <Group>
            <TsrButtonLink
              disabled={selectedMap === null}
              params={{
                fieldId,
              }}
              search={
                selectedMap !== null
                  ? {
                      mapType: PRESCRIPTION_FROM_TYPES.SOILMAP,
                      mapId: Number(selectedMap),
                    }
                  : {}
              }
              to="/fields/$fieldId/maps/prescriptions/create"
            >
              {t('prescriptions.createPrescriptionTitle')}
            </TsrButtonLink>
            <ActionsMenu items={items}>
              <ActionsMenu.Button />
            </ActionsMenu>
          </Group>
        </Group>
      }
    />
  );
}

export default SoilMap;
